import React, { useEffect, useState } from "react";
import { MessageCircle, Mail, Instagram, Send, Heart } from "lucide-react";
import { api, formatApiErrorDetail, WHATSAPP_LINK, WHATSAPP_DISPLAY, EMAIL_CONTACT } from "@/lib/api";
import { toast, Toaster } from "sonner";

export default function Contato() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const [testimonial, setTestimonial] = useState({ name: "", message: "" });
  const [tSubmitting, setTSubmitting] = useState(false);

  useEffect(() => {
    if (window.location.hash === "#depoimento") {
      setTimeout(() => {
        document.getElementById("depoimento")?.scrollIntoView({ behavior: "smooth" });
      }, 200);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Mensagem enviada com cuidado. Em breve entrarei em contato.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao enviar.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleTestimonial = async (e) => {
    e.preventDefault();
    setTSubmitting(true);
    try {
      await api.post("/testimonials", testimonial);
      toast.success("Obrigada pelo seu depoimento! Será publicado após aprovação.");
      setTestimonial({ name: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao enviar.");
    } finally {
      setTSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contato">
      <Toaster position="top-right" richColors />
      <section className="section">
        <div className="container-narrow grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <span className="text-eyebrow">Contato</span>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl mt-5 leading-[1.1] text-[#2C352D]">
              Vamos conversar.
            </h1>
            <p className="mt-6 text-[#5B685C] text-lg font-light leading-relaxed">
              Escolha o canal que for mais confortável para você. Respondo com cuidado a cada mensagem.
            </p>

            <div className="mt-10 space-y-5">
              <a
                data-testid="contato-whatsapp-card"
                href={WHATSAPP_LINK()}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-[#8BA88E]/15 soft-shadow hover-shadow transition-all"
              >
                <div className="h-12 w-12 rounded-full bg-[#25D366]/15 text-[#25D366] flex items-center justify-center">
                  <MessageCircle size={22} />
                </div>
                <div>
                  <p className="text-eyebrow">WhatsApp</p>
                  <p className="font-serif text-xl text-[#2C352D]">{WHATSAPP_DISPLAY}</p>
                </div>
              </a>
              <a
                data-testid="contato-email-card"
                href={`mailto:${EMAIL_CONTACT}`}
                className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-[#8BA88E]/15 soft-shadow hover-shadow transition-all"
              >
                <div className="h-12 w-12 rounded-full bg-[#B4C5E4]/30 text-[#5B7BAA] flex items-center justify-center">
                  <Mail size={22} />
                </div>
                <div>
                  <p className="text-eyebrow">E-mail</p>
                  <p className="font-serif text-lg text-[#2C352D] break-all">{EMAIL_CONTACT}</p>
                </div>
              </a>
              <a
                data-testid="contato-instagram-card"
                href="https://instagram.com/psicanalisecomcristina"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-[#8BA88E]/15 soft-shadow hover-shadow transition-all"
              >
                <div className="h-12 w-12 rounded-full bg-[#8BA88E]/15 text-[#8BA88E] flex items-center justify-center">
                  <Instagram size={22} />
                </div>
                <div>
                  <p className="text-eyebrow">Instagram</p>
                  <p className="font-serif text-xl text-[#2C352D]">@psicanalisecomcristina</p>
                </div>
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              data-testid="contato-form"
              onSubmit={handleSubmit}
              className="bg-white rounded-3xl p-8 md:p-10 border border-[#8BA88E]/15 soft-shadow"
            >
              <h2 className="font-serif text-3xl text-[#2C352D]">Envie sua mensagem</h2>
              <p className="text-[#5B685C] font-light mt-2">Responderei o mais breve possível.</p>

              <div className="grid sm:grid-cols-2 gap-5 mt-8">
                <div>
                  <label className="text-sm text-[#5B685C] font-medium">Nome</label>
                  <input
                    data-testid="contato-input-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2 w-full rounded-xl border border-[#8BA88E]/30 bg-[#FAF9F6] px-4 py-3 outline-none focus:border-[#8BA88E] focus:ring-2 focus:ring-[#8BA88E]/20 font-light"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#5B685C] font-medium">E-mail</label>
                  <input
                    data-testid="contato-input-email"
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-2 w-full rounded-xl border border-[#8BA88E]/30 bg-[#FAF9F6] px-4 py-3 outline-none focus:border-[#8BA88E] focus:ring-2 focus:ring-[#8BA88E]/20 font-light"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#5B685C] font-medium">Telefone (opcional)</label>
                  <input
                    data-testid="contato-input-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-2 w-full rounded-xl border border-[#8BA88E]/30 bg-[#FAF9F6] px-4 py-3 outline-none focus:border-[#8BA88E] focus:ring-2 focus:ring-[#8BA88E]/20 font-light"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#5B685C] font-medium">Assunto</label>
                  <input
                    data-testid="contato-input-subject"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="mt-2 w-full rounded-xl border border-[#8BA88E]/30 bg-[#FAF9F6] px-4 py-3 outline-none focus:border-[#8BA88E] focus:ring-2 focus:ring-[#8BA88E]/20 font-light"
                  />
                </div>
              </div>
              <div className="mt-5">
                <label className="text-sm text-[#5B685C] font-medium">Mensagem</label>
                <textarea
                  data-testid="contato-input-message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-2 w-full rounded-xl border border-[#8BA88E]/30 bg-[#FAF9F6] px-4 py-3 outline-none focus:border-[#8BA88E] focus:ring-2 focus:ring-[#8BA88E]/20 font-light resize-none"
                />
              </div>
              <button
                data-testid="contato-submit-btn"
                disabled={submitting}
                className="btn-primary mt-8 disabled:opacity-60"
              >
                <Send size={16} /> {submitting ? "Enviando..." : "Enviar mensagem"}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section id="depoimento" className="section bg-[#F5F5ED]">
        <div className="container-narrow max-w-3xl text-center">
          <Heart size={28} className="text-[#8BA88E] mx-auto" />
          <span className="text-eyebrow block mt-4">Deixe seu depoimento</span>
          <h2 className="font-serif text-4xl md:text-5xl mt-5 text-[#2C352D] leading-tight">
            Se a análise te ajudou,<br /> compartilhe sua experiência.
          </h2>
          <p className="mt-6 text-[#5B685C] font-light">
            Seu depoimento ajuda outras pessoas a darem o primeiro passo. Toda publicação passa por minha
            aprovação, preservando o seu cuidado e o nosso vínculo.
          </p>

          <form
            data-testid="testimonial-form"
            onSubmit={handleTestimonial}
            className="mt-10 bg-white rounded-3xl p-8 md:p-10 border border-[#8BA88E]/15 soft-shadow text-left"
          >
            <div className="grid sm:grid-cols-1 gap-5">
              <div>
                <label className="text-sm text-[#5B685C] font-medium">Como deseja assinar?</label>
                <input
                  data-testid="testimonial-input-name"
                  required
                  value={testimonial.name}
                  onChange={(e) => setTestimonial({ ...testimonial, name: e.target.value })}
                  placeholder="Ex: Maria, 32 anos · ou Anônimo"
                  className="mt-2 w-full rounded-xl border border-[#8BA88E]/30 bg-[#FAF9F6] px-4 py-3 outline-none focus:border-[#8BA88E] focus:ring-2 focus:ring-[#8BA88E]/20 font-light"
                />
              </div>
              <div>
                <label className="text-sm text-[#5B685C] font-medium">Seu depoimento</label>
                <textarea
                  data-testid="testimonial-input-message"
                  required
                  rows={5}
                  value={testimonial.message}
                  onChange={(e) => setTestimonial({ ...testimonial, message: e.target.value })}
                  className="mt-2 w-full rounded-xl border border-[#8BA88E]/30 bg-[#FAF9F6] px-4 py-3 outline-none focus:border-[#8BA88E] focus:ring-2 focus:ring-[#8BA88E]/20 font-light resize-none"
                />
              </div>
            </div>
            <button
              data-testid="testimonial-submit-btn"
              disabled={tSubmitting}
              className="btn-primary mt-8 disabled:opacity-60"
            >
              <Send size={16} /> {tSubmitting ? "Enviando..." : "Enviar depoimento"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
